const TOKEN_KEY = 'invit.tokens';

export const tokens = {
  get: () => { try { return JSON.parse(localStorage.getItem(TOKEN_KEY)) || null; } catch { return null; } },
  set: (t) => localStorage.setItem(TOKEN_KEY, JSON.stringify(t)),
  clear: () => localStorage.removeItem(TOKEN_KEY),
  /**
   * The access token itself says whether its holder carries a temporary
   * password (`mcp`), so the flag survives even when a response leaves it out.
   */
  mustChangePassword: () => {
    const t = tokens.get();
    if (!t) return false;
    if (t.must_change_password) return true;
    try {
      const part = String(t.access_token || '').split('.')[1];
      if (!part) return false;
      const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
      return Boolean(payload.mcp);
    } catch { return false; }
  },
};

/** App listens for these: the session ended, or a temporary password must be replaced first. */
export const SIGNED_OUT_EVENT = 'invit:signed-out';
export const PASSWORD_CHANGE_EVENT = 'invit:password-change-required';

export class ApiError extends Error {
  constructor(status, body) {
    super((body && body.message) || `Request failed (${status})`);
    this.status = status;
    this.code = body && body.error;
    this.details = body && body.details;
  }
}

/**
 * "Request validation failed" on its own tells nobody which field is wrong.
 * The server sends the offending paths; put them in front of the person.
 */
export const errorText = (err) => {
  const details = err && err.details;
  if (!Array.isArray(details) || details.length === 0) return err.message;
  return `${err.message}: ${details.map((d) => `${d.path} â€” ${d.message}`).join('; ')}`;
};

// One refresh at a time: every request that hit a 401 together waits on the same one.
let refreshing = null;
const refreshTokens = () => {
  if (!refreshing) {
    refreshing = (async () => {
      const t = tokens.get();
      if (!t || !t.refresh_token) return false;
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refresh_token: t.refresh_token }),
        });
        if (!res.ok) {
          // Refresh tokens are single use. Another tab may have spent this one
          // a moment ago and stored its successor; that is not a sign-out.
          const now = tokens.get();
          return Boolean(now && now.refresh_token && now.refresh_token !== t.refresh_token);
        }
        const fresh = await res.json();
        if (!fresh || !fresh.access_token) return false;
        tokens.set({ ...t, ...fresh });
        return true;
      } catch { return false; }
    })().finally(() => { refreshing = null; });
  }
  return refreshing;
};

const signedOut = () => {
  tokens.clear();
  window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
};

const NO_REFRESH = ['/api/auth/login', '/api/auth/refresh', '/api/auth/logout', '/api/auth/forgot-password', '/api/auth/reset-password'];

async function send(method, path, body) {
  const t = tokens.get();
  return fetch(path, {
    method,
    headers: {
      ...(t ? { authorization: `Bearer ${t.access_token}` } : {}),
      ...(body ? { 'content-type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

async function request(method, path, body, { raw = false } = {}) {
  let res = await send(method, path, body);
  if (res.status === 401 && tokens.get() && !NO_REFRESH.some((p) => path.startsWith(p))) {
    if (await refreshTokens()) res = await send(method, path, body);
    if (res.status === 401) signedOut();
  }
  if (raw && res.ok) return res;
  const text = await res.text();
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch { json = null; }
  if (!res.ok) {
    if (res.status === 403 && json && json.error === 'password_change_required') {
      window.dispatchEvent(new Event(PASSWORD_CHANGE_EVENT));
    }
    throw new ApiError(res.status, json);
  }
  return json;
}

/**
 * A new window cannot carry the Bearer header, so a document behind /api is
 * fetched here and handed over as an object URL. The window is opened first,
 * while the click still counts as the person's own, or pop-up blockers eat it.
 * A caller with work to do before the document exists opens the window itself
 * (blankWindow) at the click and passes it in.
 */
export const blankWindow = () => {
  const win = window.open('', '_blank');
  if (win) {
    try { win.document.title = 'Loading…'; win.document.body.textContent = 'Loading…'; } catch { /* not ready */ }
  }
  return win;
};

export async function openAuthed(path, preopened) {
  const win = preopened === undefined ? blankWindow() : preopened;
  try {
    const res = await request('GET', path, undefined, { raw: true });
    const url = URL.createObjectURL(await res.blob());
    if (win) win.location.href = url;
    else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (win) win.close();
    throw e;
  }
}

export const api = {
  get: (p) => request('GET', p),
  post: (p, b) => request('POST', p, b),
  put: (p, b) => request('PUT', p, b),
  patch: (p, b) => request('PATCH', p, b),
  delete: (p) => request('DELETE', p),
  raw: (p) => request('GET', p, undefined, { raw: true }),
  // A POST whose answer is a file, not JSON (an Excel export).
  postRaw: (p, b) => request('POST', p, b, { raw: true }),
  login: async (email, password) => {
    const res = await request('POST', '/api/auth/login', { email, password });
    tokens.set(res);
    return res;
  },
  // The server revokes the refresh token's family, so a copy left behind on a
  // shared computer stops working with this sign-out rather than in 30 days.
  logout: () => {
    const t = tokens.get();
    tokens.clear();
    if (t && t.refresh_token) {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refresh_token: t.refresh_token }),
      }).catch(() => {});
    }
  },
  open: (p, win) => openAuthed(p, win),
};

export const money = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const qty = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
/** Compact Indian currency for tiles: 9,74,680 -> "9.75 L", 2.4 crore -> "2.40 Cr". */
export const compactMoney = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e7) return `${(v / 1e7).toFixed(2)} Cr`;
  if (Math.abs(v) >= 1e5) return `${(v / 1e5).toFixed(2)} L`;
  return money(v);
};

export const day = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');
